// Canonical lead schema — flat fields per API contract.
// Statuses: New | Contacted | Qualified | Lost | Converted | Nurturing | Rejected
// Priorities: Low | Medium | High | Urgent

export const LEAD_STATUSES = ['New', 'Contacted', 'Qualified', 'Nurturing', 'Converted', 'Lost', 'Rejected'];
export const LEAD_PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

export const STATUS_TONE = {
  New: 'sky',
  Contacted: 'indigo',
  Qualified: 'violet',
  Nurturing: 'amber',
  Converted: 'emerald',
  Lost: 'rose',
  Rejected: 'slate',
};

export const OWNER_TYPE_LABELS = {
  agent: 'Agent Owned',
  queue: 'Queue Owned',
  unassigned: 'Unassigned',
};

function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function normalizeAssignmentHistory(history = []) {
  if (!Array.isArray(history)) {
    return [];
  }

  return history.map((item) => ({
    assignedTo: item.assigned_to || null,
    ownerType: item.owner_type || 'unassigned',
    assignedAt: item.assigned_at || null,
    assignedAtLabel: formatDateTime(item.assigned_at),
    ruleId: item.rule_id || null,
    method: item.method || null,
    reason: item.reason || '',
  }));
}

function getSlaState(value) {
  if (!value) {
    return 'none';
  }

  const dueAt = new Date(value);
  if (Number.isNaN(dueAt.getTime())) {
    return 'none';
  }

  if (dueAt.getTime() <= Date.now()) {
    return 'breached';
  }

  const fourHoursMs = 4 * 60 * 60 * 1000;
  if (dueAt.getTime() - Date.now() <= fourHoursMs) {
    return 'due-soon';
  }

  return 'on-track';
}

// Normalize a raw lead object returned from GET /api/leads or GET /api/leads/:id
export function normalizeLead(lead) {
  const fullName =
    lead.full_name ||
    [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
    '—';
  const ownerType = lead.owner_type || 'unassigned';
  const assignmentQueueId = lead.assignment_queue_id || null;
  const slaState = getSlaState(lead.sla_due_at);

  return {
    id: lead.id || lead._id,
    fullName,
    firstName: lead.first_name || '',
    lastName: lead.last_name || '',
    email: lead.email || '—',
    phone: lead.phone || '—',
    source: lead.lead_source || '—',
    priority: lead.priority || 'Medium',
    status: lead.status || 'New',
    statusTone: STATUS_TONE[lead.status] || 'slate',
    assignedTo: lead.assigned_to || null,
    ownerType,
    ownerTypeLabel: OWNER_TYPE_LABELS[ownerType] || OWNER_TYPE_LABELS.unassigned,
    assignmentRuleId: lead.assignment_rule_id || null,
    assignmentQueueId,
    assignmentQueueLabel: assignmentQueueId || '—',
    slaDueAt: lead.sla_due_at || null,
    slaDueAtLabel: formatDateTime(lead.sla_due_at),
    slaState,
    claimedBy: lead.claimed_by || null,
    claimedAt: lead.claimed_at || null,
    claimedAtLabel: formatDateTime(lead.claimed_at),
    assignmentHistory: normalizeAssignmentHistory(lead.assignment_history),
    isConverted: !!lead.is_converted,
    leadScore: lead.lead_score ?? null,
    leadClassification: lead.lead_classification || null,
    companyName: lead.company_name || '—',
    createdAt: lead.created_at,
    updatedAt: lead.updated_at,
    createdAtLabel: formatDate(lead.created_at),
    updatedAtLabel: formatDate(lead.updated_at),
    raw: lead,
  };
}

// Build flat payload for POST /api/leads
// Required by backend: first_name, last_name, email, company_name, lead_source
export function buildCreateLeadPayload(form) {
  const payload = {
    first_name: form.first_name,
    last_name: form.last_name,
    email: form.email,
    company_name: form.company_name,
    lead_source: form.lead_source,
  };
  if (form.phone) payload.phone = form.phone;
  if (form.priority) payload.priority = form.priority;
  if (form.status) payload.status = form.status;
  if (form.notes) payload.notes = form.notes;
  return payload;
}

// Build flat payload for PUT /api/leads/:id (partial update)
export function buildUpdateLeadPayload(form) {
  const payload = {};
  const fields = [
    'first_name', 'last_name', 'email', 'phone', 'company_name',
    'lead_source', 'priority', 'status', 'notes', 'assigned_to',
  ];
  fields.forEach((key) => {
    if (form[key] !== undefined && form[key] !== '') {
      payload[key] = form[key];
    }
  });
  return payload;
}
